import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { 
  Users, Pencil, Power, PowerOff, RefreshCw, Search, ChevronLeft, 
  ChevronRight, AlertCircle, Shield, Mail, Phone, UserPlus, CheckCircle2,
  LayoutGrid, List, Building, Briefcase, Calendar, MessageSquare, Crown, HardHat, PenTool, Wallet, Eye
} from 'lucide-react';
import { 
  fetchProjectMembers, deactivateMember, restoreMember, 
  changeMemberRole, resendInvitation 
} from '../../api/projectMember';
import type { ProjectMember } from '../../types/projectMember';
import Modal from '../Modal';
import toast from 'react-hot-toast';
import { useDebounce } from '../../hooks/useDebounce';

const CANONICAL_ROLES = [
  { value: 'owner', label: 'Owner', desc: 'Pemilik proyek (BUMN/Swasta)', icon: Crown },
  { value: 'manager', label: 'Manager', desc: 'Project Manager / Lead', icon: Briefcase },
  { value: 'contractor', label: 'Contractor', desc: 'Pelaksana teknis / Kontraktor', icon: HardHat },
  { value: 'architect', label: 'Architect', desc: 'Perencana / Arsitek', icon: PenTool },
  { value: 'finance', label: 'Finance', desc: 'Pengelola Keuangan', icon: Wallet },
  { value: 'viewer', label: 'Viewer', desc: 'Akses lihat saja', icon: Eye },
];

const STATUS_OPTIONS = [
  { value: '', label: 'Semua Status' },
  { value: 'active', label: 'Aktif' },
  { value: 'pending', label: 'Pending (Undangan)' },
  { value: 'expired', label: 'Kadaluarsa' },
  { value: 'inactive', label: 'Non-aktif / Removed' },
];

const getStatusBadge = (status: string) => {
  switch (status?.toLowerCase()) {
    case 'active':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
    case 'pending':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
    case 'expired':
      return 'bg-rose-50 text-rose-700 ring-1 ring-rose-600/20';
    case 'inactive':
    case 'removed':
      return 'bg-slate-100 text-slate-500 ring-1 ring-slate-500/20';
    default:
      return 'bg-slate-100 text-slate-600';
  }
};

const getRoleBadge = (role: string) => {
  switch (role?.toLowerCase()) {
    case 'owner':
      return 'bg-purple-50 text-purple-700 ring-1 ring-purple-600/20';
    case 'manager':
      return 'bg-blue-50 text-blue-700 ring-1 ring-blue-600/20';
    case 'contractor':
      return 'bg-amber-50 text-amber-700 ring-1 ring-amber-600/20';
    case 'architect':
      return 'bg-indigo-50 text-indigo-700 ring-1 ring-indigo-600/20';
    case 'finance':
      return 'bg-emerald-50 text-emerald-700 ring-1 ring-emerald-600/20';
    default:
      return 'bg-slate-100 text-slate-600 ring-1 ring-slate-400/20';
  }
};

interface MembersTableProps {
  projectId: string;
  onNavigateToAdd?: () => void;
}

export const MembersTable: React.FC<MembersTableProps> = ({ projectId, onNavigateToAdd }) => {
  const [viewMode, setViewMode] = useState<'card' | 'table'>('card');
  const [page, setPage] = useState(1);
  const limit = 12;
  const [statusFilter, setStatusFilter] = useState('');
  const [searchFilter, setSearchFilter] = useState('');
  const debouncedSearch = useDebounce(searchFilter, 500);
  const [roleFilter, setRoleFilter] = useState('');

  // Modal States
  const [roleModalOpen, setRoleModalOpen] = useState(false);
  const [selectedMember, setSelectedMember] = useState<ProjectMember | null>(null);
  const [newRole, setNewRole] = useState('viewer');

  const [deactivateModalOpen, setDeactivateModalOpen] = useState(false);
  const [restoreModalOpen, setRestoreModalOpen] = useState(false);
  const [resendModalOpen, setResendModalOpen] = useState(false);

  const queryClient = useQueryClient();

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ['projectMembers', projectId, page, limit, statusFilter, debouncedSearch, roleFilter],
    queryFn: () => fetchProjectMembers(projectId, page, limit, statusFilter || undefined, debouncedSearch || undefined, roleFilter || undefined),
    staleTime: 1000 * 30,
  });

  // Fetch all members to check if owner/manager already exists
  const { data: allMembersData } = useQuery({
    queryKey: ['projectMembers', projectId, 'all'],
    queryFn: () => fetchProjectMembers(projectId, 1, 1000),
    staleTime: 1000 * 30,
  });

  const allMembers = allMembersData?.data || [];
  const hasOwner = allMembers.some((m: any) => m.role === 'owner' && m.status !== 'inactive' && m.status !== 'removed' && m.id !== selectedMember?.id);
  const hasManager = allMembers.some((m: any) => m.role === 'manager' && m.status !== 'inactive' && m.status !== 'removed' && m.id !== selectedMember?.id);

  const availableRoles = CANONICAL_ROLES.filter((r) => {
    if (r.value === 'owner' && hasOwner) return false;
    if (r.value === 'manager' && hasManager) return false;
    return true;
  });

  // Mutations
  const roleChangeMut = useMutation({
    mutationFn: ({ memberId, role }: { memberId: string; role: string }) =>
      changeMemberRole(projectId, memberId, role),
    onSuccess: () => {
      toast.success('Role anggota berhasil diperbarui');
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setRoleModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Gagal mengubah role');
    },
  });

  const deactivateMut = useMutation({
    mutationFn: (memberId: string) => deactivateMember(projectId, memberId),
    onSuccess: () => {
      toast.success('Anggota berhasil dinonaktifkan');
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setDeactivateModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Gagal menonaktifkan anggota');
    },
  });

  const restoreMut = useMutation({
    mutationFn: (memberId: string) => restoreMember(projectId, memberId),
    onSuccess: () => {
      toast.success('Anggota berhasil diaktifkan kembali');
      queryClient.invalidateQueries({ queryKey: ['projectMembers'] });
      setRestoreModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Gagal mengaktifkan kembali anggota');
    },
  });

  const resendMut = useMutation({
    mutationFn: ({ memberId, via }: { memberId: string; via: 'email' | 'whatsapp' }) => resendInvitation(projectId, memberId, via),
    onSuccess: (data, variables) => {
      if (variables.via === 'whatsapp') {
        const member = members.find((m: any) => m.id === variables.memberId);
        const name = member?.contact_snapshot?.name || 'Anggota';
        const phone = member?.contact_snapshot?.phone;
        const role = member?.role || 'viewer';
        const roleLabel = CANONICAL_ROLES.find(r => r.value === role)?.label || role;
        
        let cleanPhone = phone ? phone.replace(/[^0-9]/g, '') : '';
        if (cleanPhone.startsWith('0')) {
          cleanPhone = '62' + cleanPhone.substring(1);
        }

        const token = data?.content?.token || data?.data?.token || data?.token || '';
        
        if (cleanPhone && token) {
          const acceptUrl = `${window.location.origin}/invitations/accept/${token}`;
          const waText = `Halo *${name}*,\n\nKami mengirim ulang undangan Anda untuk bergabung dalam *Tim Proyek MANPRO* sebagai *${roleLabel}*.\n\nSilakan klik tautan berikut untuk menerima undangan:\n${acceptUrl}\n\nTerima kasih,\nTim Indoteknisi MANPRO`;
          
          const waUrl = `https://wa.me/${cleanPhone}?text=${encodeURIComponent(waText)}`;
          const waWindow = window.open(waUrl, '_blank');
          
          if (!waWindow || waWindow.closed || typeof waWindow.closed === 'undefined') {
            toast.error('Browser memblokir popup. Silakan izinkan popup untuk membuka WhatsApp.', { duration: 5000 });
            toast.success('Undangan berhasil di-generate.', { duration: 3000 });
          } else {
            toast.success('Undangan berhasil di-generate. Membuka WhatsApp...');
          }
        } else {
          toast.success('Berhasil, namun nomor telepon tidak tersedia untuk membuka WA.');
        }
      } else {
        toast.success('Email undangan berhasil dikirim ulang');
      }
      setResendModalOpen(false);
    },
    onError: (err: any) => {
      toast.error(err?.response?.data?.message || 'Gagal mengirim ulang undangan');
    },
  });

  const members = data?.data ?? [];
  const meta = data?.meta;


  if (isError) {
    return (
      <div className="flex flex-col items-center justify-center py-16 text-slate-500">
        <AlertCircle size={48} className="mb-3 text-rose-500" />
        <p className="text-lg font-bold text-slate-800">Gagal Memuat Data Tim</p>
        <p className="text-sm text-slate-500 mb-4">Terjadi kesalahan saat mengambil daftar anggota proyek.</p>
        <button
          onClick={() => refetch()}
          className="px-4 py-2 text-sm font-semibold rounded-xl bg-slate-900 text-white hover:bg-slate-800 transition"
        >
          Coba Lagi
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {/* Control Bar: Filter, View Mode Toggle, Add Button */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <input 
              type="text" 
              placeholder="Cari nama atau email..."
              value={searchFilter}
              onChange={(e) => {
                setSearchFilter(e.target.value);
                setPage(1);
              }}
              className="pl-9 pr-4 py-2.5 w-48 text-sm bg-slate-50 border border-slate-200/80 rounded-xl text-slate-700 outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all"
            />
          </div>

          <select
            value={roleFilter}
            onChange={(e) => {
              setRoleFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2.5 text-sm bg-slate-50 border border-slate-200/80 rounded-xl text-slate-700 font-medium outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
          >
            <option value="">Semua Role</option>
            {CANONICAL_ROLES.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="px-4 py-2.5 text-sm bg-slate-50 border border-slate-200/80 rounded-xl text-slate-700 font-medium outline-none focus:ring-2 focus:ring-blue-500/20 focus:border-blue-500 transition-all cursor-pointer"
          >
            {STATUS_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>

          <span className="text-xs font-semibold text-slate-400">
            Total: {meta?.total ?? members.length} Anggota
          </span>
        </div>

        <div className="flex items-center gap-2">
          {/* View Mode Toggle: Cards vs Table */}
          <div className="flex items-center p-1 bg-slate-100 rounded-xl border border-slate-200/80">
            <button
              onClick={() => setViewMode('card')}
              title="Tampilan Kartu (Grid)"
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'card'
                  ? 'bg-white text-slate-900 shadow-xs font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <LayoutGrid size={16} />
            </button>
            <button
              onClick={() => setViewMode('table')}
              title="Tampilan Tabel"
              className={`p-2 rounded-lg transition-all ${
                viewMode === 'table'
                  ? 'bg-white text-slate-900 shadow-xs font-semibold'
                  : 'text-slate-500 hover:text-slate-800'
              }`}
            >
              <List size={16} />
            </button>
          </div>

          {onNavigateToAdd && (
            <button
              onClick={onNavigateToAdd}
              className="inline-flex items-center justify-center gap-2 px-4 py-2.5 text-sm font-semibold rounded-xl bg-blue-600 text-white shadow-md shadow-blue-500/20 hover:bg-blue-700 transition-all duration-200"
            >
              <UserPlus size={16} />
              <span>Tambah Anggota</span>
            </button>
          )}
        </div>
      </div>

      {/* Main Content: Card Grid View or Table View */}
      {isLoading ? (
        <div className={viewMode === 'card' ? "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5" : "space-y-3"}>
          {[...Array(6)].map((_, i) => (
            <div key={i} className={`${viewMode === 'card' ? 'h-48' : 'h-16'} bg-slate-50 border border-slate-100 rounded-3xl animate-pulse`} />
          ))}
        </div>
      ) : members.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-16 text-center border-2 border-dashed border-slate-200 rounded-3xl bg-slate-50/50">
          <div className="p-4 rounded-full bg-slate-100 text-slate-400 mb-3">
            <Users size={36} />
          </div>
          <p className="text-base font-bold text-slate-700">Belum Ada Anggota</p>
          <p className="text-xs text-slate-400 max-w-sm mt-1 mb-4">
            Tidak ada data anggota tim yang sesuai dengan filter yang dipilih.
          </p>
          {onNavigateToAdd && (
            <button
              onClick={onNavigateToAdd}
              className="inline-flex items-center gap-2 px-4 py-2 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition"
            >
              <UserPlus size={16} />
              Tambah Sekarang
            </button>
          )}
        </div>
      ) : viewMode === 'card' ? (
        /* CARD GRID VIEW */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {members.map((m) => {
            const name = m.user?.name || m.contact_snapshot?.name || 'Tanpa Nama';
            const email = m.user?.email || m.contact_snapshot?.email || '-';
            const phone = m.user?.phone || m.contact_snapshot?.phone || '-';
            const company = m.contact_snapshot?.company;
            const jobTitle = m.contact_snapshot?.job_title;
            const isDeactivated = m.status === 'inactive' || m.status === 'removed';
            const isPending = m.status === 'pending' || m.status === 'expired';

            return (
              <div
                key={m.id}
                className="group relative flex flex-col justify-between rounded-3xl border border-slate-200/80 bg-white p-5 shadow-xs hover:shadow-md hover:border-slate-300 transition-all duration-200"
              >
                <div>
                  {/* Card Header: Avatar, Name, Status */}
                  <div className="flex items-start justify-between gap-3 mb-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div className="w-12 h-12 rounded-2xl bg-gradient-to-tr from-slate-900 via-slate-800 to-indigo-700 flex items-center justify-center text-white font-extrabold text-base shadow-sm">
                          {name.charAt(0).toUpperCase()}
                        </div>
                        {/* Status Dot */}
                        <span
                          className={`absolute -bottom-1 -right-1 w-3.5 h-3.5 rounded-full border-2 border-white ${
                            m.status === 'active'
                              ? 'bg-emerald-500'
                              : m.status === 'pending'
                              ? 'bg-amber-500'
                              : m.status === 'expired'
                              ? 'bg-rose-500'
                              : 'bg-slate-400'
                          }`}
                        />
                      </div>

                      <div>
                        <h3 className="font-bold text-slate-800 text-base leading-snug group-hover:text-blue-600 transition-colors">
                          {name}
                        </h3>
                        {jobTitle ? (
                          <div className="flex items-center gap-1 text-xs text-slate-500 font-medium">
                            <Briefcase size={12} className="text-slate-400" />
                            <span className="truncate max-w-[160px]">{jobTitle}</span>
                          </div>
                        ) : (
                          <span className="text-xs text-slate-400">Anggota Tim</span>
                        )}
                      </div>
                    </div>

                    {/* Status Badge */}
                    <div className="flex flex-col items-end gap-1">
                      <span
                        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-[11px] font-bold uppercase tracking-wider ${getStatusBadge(
                          m.status
                        )}`}
                      >
                        {m.status}
                      </span>
                      {isPending && m.contact_snapshot?.via && (
                        <span className="text-[10px] text-slate-400 font-medium flex items-center gap-1">
                          {m.contact_snapshot.via === 'whatsapp' ? <MessageSquare size={10} /> : <Mail size={10} />}
                          Via {m.contact_snapshot.via === 'whatsapp' ? 'WhatsApp' : 'Email'}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Card Details: Company, Email, Phone */}
                  <div className="space-y-2 py-3 border-y border-slate-100 text-xs text-slate-600 mb-4">
                    {company && (
                      <div className="flex items-center gap-2">
                        <Building size={13} className="text-slate-400 flex-shrink-0" />
                        <span className="font-medium text-slate-700 truncate">{company}</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <Mail size={13} className="text-slate-400 flex-shrink-0" />
                      <span className="truncate text-slate-600 font-medium">{email}</span>
                    </div>
                    {phone !== '-' && (
                      <div className="flex items-center gap-2">
                        <Phone size={13} className="text-slate-400 flex-shrink-0" />
                        <span className="text-slate-500">{phone}</span>
                      </div>
                    )}
                  </div>
                </div>

                {/* Card Footer: Role & Action Buttons */}
                <div className="flex items-center justify-between gap-2 pt-1">
                  <span
                    className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold capitalize ${getRoleBadge(
                      m.role
                    )}`}
                  >
                    <Shield size={12} />
                    {m.role}
                  </span>

                  <div className="flex items-center gap-1">
                    {/* Ubah Role */}
                    <button
                      onClick={() => {
                        setSelectedMember(m);
                        setNewRole(m.role);
                        setRoleModalOpen(true);
                      }}
                      title="Ubah Role"
                      className="p-2 rounded-xl text-slate-400 hover:text-blue-600 hover:bg-blue-50 transition-all"
                    >
                      <Pencil size={15} />
                    </button>

                    {/* Resend email if pending/expired */}
                    {isPending && (
                      <button
                        onClick={() => {
                          setSelectedMember(m);
                          setResendModalOpen(true);
                        }}
                        title="Kirim Ulang Undangan"
                        className="p-2 rounded-xl text-slate-400 hover:text-amber-600 hover:bg-amber-50 transition-all"
                      >
                        <RefreshCw size={15} />
                      </button>
                    )}

                    {/* Non-aktifkan / Restore */}
                    {!isDeactivated ? (
                      <button
                        onClick={() => {
                          setSelectedMember(m);
                          setDeactivateModalOpen(true);
                        }}
                        title="Non-aktifkan Anggota"
                        className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition-all"
                      >
                        <PowerOff size={15} />
                      </button>
                    ) : (
                      <button
                        onClick={() => {
                          setSelectedMember(m);
                          setRestoreModalOpen(true);
                        }}
                        title="Aktifkan Kembali"
                        className="p-2 rounded-xl text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                      >
                        <Power size={15} />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        /* TABLE VIEW */
        <div className="overflow-hidden rounded-2xl border border-slate-200/80 bg-white shadow-xs">
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="border-b border-slate-200/80 bg-slate-50/80 text-xs font-bold uppercase tracking-wider text-slate-500">
                  <th className="px-5 py-3.5">Nama & Profil</th>
                  <th className="px-5 py-3.5">Kontak</th>
                  <th className="px-5 py-3.5">Role (Hak Akses)</th>
                  <th className="px-5 py-3.5">Status</th>
                  <th className="px-5 py-3.5 text-center">Aksi</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100 text-sm">
                {members.map((m) => {
                  const name = m.user?.name || m.contact_snapshot?.name || 'Tanpa Nama';
                  const email = m.user?.email || m.contact_snapshot?.email || '-';
                  const phone = m.user?.phone || m.contact_snapshot?.phone || '-';
                  const isDeactivated = m.status === 'inactive' || m.status === 'removed';
                  const isPending = m.status === 'pending' || m.status === 'expired';

                  return (
                    <tr key={m.id} className="hover:bg-slate-50/60 transition-colors">
                      <td className="px-5 py-4">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-gradient-to-tr from-slate-800 to-indigo-600 flex items-center justify-center text-white font-bold text-sm shadow-xs flex-shrink-0">
                            {name.charAt(0).toUpperCase()}
                          </div>
                          <div>
                            <div className="font-bold text-slate-800">{name}</div>
                            {m.contact_snapshot?.job_title && (
                              <div className="text-xs text-slate-400 font-medium">
                                {m.contact_snapshot.job_title} {m.contact_snapshot.company ? `• ${m.contact_snapshot.company}` : ''}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>

                      <td className="px-5 py-4 text-slate-600">
                        <div className="flex items-center gap-1.5 text-xs font-medium">
                          <Mail size={13} className="text-slate-400 flex-shrink-0" />
                          <span className="truncate max-w-[180px]">{email}</span>
                        </div>
                        {phone !== '-' && (
                          <div className="flex items-center gap-1.5 text-xs text-slate-400 mt-1">
                            <Phone size={13} className="flex-shrink-0" />
                            <span>{phone}</span>
                          </div>
                        )}
                      </td>

                      <td className="px-5 py-4">
                        <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-bold capitalize ${getRoleBadge(m.role)}`}>
                          <Shield size={12} />
                          {m.role}
                        </span>
                      </td>

                      <td className="px-5 py-4">
                        <div className="flex flex-col items-start gap-1">
                          <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider ${getStatusBadge(m.status)}`}>
                            <span className="w-1.5 h-1.5 rounded-full bg-current" />
                            {m.status}
                          </span>
                          {isPending && m.contact_snapshot?.via && (
                            <span className="text-[10px] text-slate-400 font-medium ml-1 flex items-center gap-1">
                              {m.contact_snapshot.via === 'whatsapp' ? <MessageSquare size={10} /> : <Mail size={10} />}
                              Via {m.contact_snapshot.via === 'whatsapp' ? 'WhatsApp' : 'Email'}
                            </span>
                          )}
                        </div>
                      </td>

                      <td className="px-5 py-4 text-center">
                        <div className="flex items-center justify-center gap-1">
                          <button
                            onClick={() => {
                              setSelectedMember(m);
                              setNewRole(m.role);
                              setRoleModalOpen(true);
                            }}
                            title="Ubah Role"
                            className="p-2 rounded-xl text-slate-500 hover:text-blue-600 hover:bg-blue-50 transition-all"
                          >
                            <Pencil size={15} />
                          </button>

                          {isPending && (
                            <button
                              onClick={() => {
                                setSelectedMember(m);
                                setResendModalOpen(true);
                              }}
                              title="Kirim Ulang Email Undangan"
                              className="p-2 rounded-xl text-slate-500 hover:text-amber-600 hover:bg-amber-50 transition-all"
                            >
                              <RefreshCw size={15} />
                            </button>
                          )}

                          {!isDeactivated ? (
                            <button
                              onClick={() => {
                                setSelectedMember(m);
                                setDeactivateModalOpen(true);
                              }}
                              title="Non-aktifkan Anggota"
                              className="p-2 rounded-xl text-slate-500 hover:text-rose-600 hover:bg-rose-50 transition-all"
                            >
                              <PowerOff size={15} />
                            </button>
                          ) : (
                            <button
                              onClick={() => {
                                setSelectedMember(m);
                                setRestoreModalOpen(true);
                              }}
                              title="Aktifkan Kembali"
                              className="p-2 rounded-xl text-slate-500 hover:text-emerald-600 hover:bg-emerald-50 transition-all"
                            >
                              <Power size={15} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Pagination Controls */}
      {meta && meta.last_page > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3 pt-2">
          <div className="text-xs text-slate-500 font-medium">
            Halaman <span className="font-bold text-slate-800">{meta.current_page}</span> dari{' '}
            <span className="font-bold text-slate-800">{meta.last_page}</span>
          </div>

          <div className="flex items-center gap-1.5">
            <button
              disabled={page <= 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition"
            >
              <ChevronLeft size={16} />
            </button>

            {(() => {
              const pages = [];
              const last = meta.last_page;
              
              if (last <= 5) {
                for (let i = 1; i <= last; i++) pages.push(i);
              } else {
                if (page <= 3) {
                  pages.push(1, 2, 3, 4, '...', last);
                } else if (page >= last - 2) {
                  pages.push(1, '...', last - 3, last - 2, last - 1, last);
                } else {
                  pages.push(1, '...', page - 1, page, page + 1, '...', last);
                }
              }
              return pages.map((p, idx) => (
                p === '...' ? (
                  <span key={`ellipsis-${idx}`} className="w-9 h-9 flex items-center justify-center text-slate-400 text-xs font-bold">...</span>
                ) : (
                  <button
                    key={p}
                    onClick={() => setPage(p as number)}
                    className={`w-9 h-9 rounded-xl text-xs font-bold transition-all ${
                      p === page
                        ? 'bg-slate-900 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    {p}
                  </button>
                )
              ));
            })()}

            <button
              disabled={page >= meta.last_page}
              onClick={() => setPage((p) => Math.min(meta.last_page, p + 1))}
              className="p-2 rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-100 disabled:opacity-40 disabled:hover:bg-transparent transition"
            >
              <ChevronRight size={16} />
            </button>
          </div>
        </div>
      )}

      {/* === MODAL UBAH ROLE === */}
      <Modal
        isOpen={roleModalOpen}
        onClose={() => setRoleModalOpen(false)}
        title="Ubah Role Anggota"
        size="md"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-500">
            Pilih role canonical baru untuk anggota tim proyek ini:
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {availableRoles.map((r) => (
              <div
                key={r.value}
                onClick={() => setNewRole(r.value)}
                className={`p-3 rounded-xl border-2 cursor-pointer transition-all ${
                  newRole === r.value
                    ? 'border-blue-500 bg-blue-50/50 shadow-sm'
                    : 'border-slate-100 bg-white hover:border-slate-200 hover:bg-slate-50'
                }`}
              >
                <div className="flex items-center justify-between mb-1.5">
                  <div className="flex items-center gap-2">
                    <r.icon size={16} className={newRole === r.value ? 'text-blue-600' : 'text-slate-400'} />
                    <span className={`font-bold text-sm ${newRole === r.value ? 'text-blue-700' : 'text-slate-800'}`}>
                      {r.label}
                    </span>
                  </div>
                  {newRole === r.value && <CheckCircle2 size={16} className="text-blue-600" />}
                </div>
                <p className="text-xs text-slate-500 leading-snug pl-6">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="flex justify-end gap-2 pt-4 border-t border-slate-100">
            <button
              onClick={() => setRoleModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Batal
            </button>
            <button
              onClick={() => {
                if (selectedMember) {
                  roleChangeMut.mutate({ memberId: selectedMember.id, role: newRole });
                }
              }}
              disabled={roleChangeMut.isPending}
              className="px-5 py-2 text-xs font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition shadow-sm disabled:opacity-50"
            >
              {roleChangeMut.isPending ? 'Simpan...' : 'Simpan Role'}
            </button>
          </div>
        </div>
      </Modal>

      {/* === MODAL NON-AKTIFKAN === */}
      <Modal
        isOpen={deactivateModalOpen}
        onClose={() => setDeactivateModalOpen(false)}
        title="Konfirmasi Non-aktifkan"
        size="sm"
      >
        <div className="space-y-4">
          <div className="p-3 rounded-2xl bg-rose-50 border border-rose-100 text-rose-800 text-xs leading-relaxed">
            Anggota yang dinonaktifkan tidak akan memiliki akses ke proyek ini hingga diaktifkan kembali.
          </div>
          <p className="text-xs text-slate-600">
            Apakah Anda yakin ingin menonaktifkan status anggota ini?
          </p>

          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={() => setDeactivateModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Batal
            </button>
            <button
              onClick={() => {
                if (selectedMember) deactivateMut.mutate(selectedMember.id);
              }}
              disabled={deactivateMut.isPending}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-rose-600 text-white hover:bg-rose-700 transition shadow-sm disabled:opacity-50"
            >
              {deactivateMut.isPending ? 'Proses...' : 'Ya, Non-aktifkan'}
            </button>
          </div>
        </div>
      </Modal>

      {/* === MODAL RESTORE === */}
      <Modal
        isOpen={restoreModalOpen}
        onClose={() => setRestoreModalOpen(false)}
        title="Aktifkan Kembali Anggota"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-xs text-slate-600">
            Aktifkan kembali akses anggota ini pada proyek?
          </p>
          <div className="flex justify-end gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={() => setRestoreModalOpen(false)}
              className="px-4 py-2 text-xs font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Batal
            </button>
            <button
              onClick={() => {
                if (selectedMember) restoreMut.mutate(selectedMember.id);
              }}
              disabled={restoreMut.isPending}
              className="px-4 py-2 text-xs font-semibold rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition shadow-sm disabled:opacity-50"
            >
              {restoreMut.isPending ? 'Proses...' : 'Ya, Aktifkan'}
            </button>
          </div>
        </div>
      </Modal>

      {/* === MODAL KIRIM ULANG UNDANGAN === */}
      <Modal
        isOpen={resendModalOpen}
        onClose={() => setResendModalOpen(false)}
        title="Kirim Ulang Undangan"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-600">
            Kirim ulang undangan verifikasi ke alamat email atau WhatsApp terdaftar?
          </p>
          <div className="flex flex-col gap-2 pt-2 border-t border-slate-100">
            <button
              onClick={() => {
                if (selectedMember) resendMut.mutate({ memberId: selectedMember.id, via: 'whatsapp' });
              }}
              disabled={resendMut.isPending}
              className="w-full py-2.5 text-sm font-semibold rounded-xl bg-green-600 text-white hover:bg-green-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Phone size={16} />
              {resendMut.isPending ? 'Mengirim...' : 'Kirim via WhatsApp'}
            </button>
            <button
              onClick={() => {
                if (selectedMember) resendMut.mutate({ memberId: selectedMember.id, via: 'email' });
              }}
              disabled={resendMut.isPending}
              className="w-full py-2.5 text-sm font-semibold rounded-xl bg-blue-600 text-white hover:bg-blue-700 transition flex items-center justify-center gap-2 disabled:opacity-50"
            >
              <Mail size={16} />
              {resendMut.isPending ? 'Mengirim...' : 'Kirim via Email'}
            </button>
            <button
              onClick={() => setResendModalOpen(false)}
              className="w-full mt-2 py-2.5 text-sm font-semibold rounded-xl border border-slate-200 text-slate-600 hover:bg-slate-50 transition"
            >
              Batal
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};
